import React, {useRef, useEffect, useState, useCallback} from 'react'
import ReactPlayer from 'react-player'
import { Typography } from '@material-ui/core'

export type ProgressState = {
  played: number,
  playedSeconds: number,
  loaded: number,
  loadedSeconds: number,
}

type Props = {
  id: string,
  url: string,
  width: number,
  height: number,
  isPlaying: boolean,
  seekTime?: number,
  playbackSpeed?: number,
  showControls?: boolean,
  intervalMs?: number,
  onVideoPlay?: (isPlaying: boolean) => void,
  onVideoLoad?: (isLoading: boolean) => void,
  onDuration?: (duration: number) => void,
  onProgress?: (progress: ProgressState) => void,
}

const VideoPlayer = (props : Props) => {
  const {
    id,
    url, 
    width, 
    height, 
    isPlaying,
    seekTime,
    playbackSpeed,
    showControls,
    intervalMs,
    onVideoPlay,
    onVideoLoad,
    onDuration,
    onProgress,
  } = props

  const ref = useRef<ReactPlayer>(null);
  const [playing, setPlaying] = useState<boolean>(isPlaying);
  const [ready, setReady] = useState<boolean>(false);
  const [canPlay, setCanPlay] = useState<boolean>(false);

  const onPlay = useCallback(() => {
    setPlaying(true);
  }, []);

  const onPause = useCallback(() => {
    setPlaying(false);
  }, []);

  const seekReady = useCallback(() => { 
    onVideoLoad && onVideoLoad(false);
  }, [onVideoLoad]);

  const onReady = useCallback(() => {
    setReady(true);
  }, []);

  const onError = useCallback((error: any, data?: any, hlsInstance?: any, hlsGlobal?: any) => {
    console.log('[VIDEO ERROR] ' + id + ": " + error, data, hlsInstance, hlsGlobal);
  }, [id]);

  // Check if is playing corresponds with our current actual playing state, if not, let upper level know
  useEffect(() => {
    if(isPlaying !== playing) {
      onVideoPlay && onVideoPlay(playing);
    }
  }, [isPlaying, onVideoPlay, playing]);

  // Handle seek
  useEffect(() => {
      if(seekTime !== undefined && seekTime >= 0 && ref && ref.current && ready) {
      //NOTE; seek granularity is to 1 decimal to avoid seeking issues in very rare cases.
      //NOTE2: if seek time = 0 then we set it to minimum of 0.1 otherwise the player won't load the video
      const timeToSeek = seekTime > 0 ? parseFloat(seekTime.toFixed(1)) : 0.1;
      ref.current.seekTo(timeToSeek, "seconds");
      onVideoLoad && onVideoLoad(true);
    }
  }, [seekTime, ready, onVideoLoad]);
  
  useEffect(() => {
    setCanPlay(ReactPlayer.canPlay(url));
  }, [url])

  if(!canPlay) {
    return <Typography>Videoplayer is not able to play url: {url}</Typography>
  }

  return (
    <ReactPlayer
      ref={ref}
      width={width}
      height={height}
      url={url}
      playing={isPlaying}
      playbackRate={playbackSpeed || 1}
      loop={false}
      muted={true}
      onReady={onReady}
      onError={onError}
      //onBuffer={() => onIsBuffering && onIsBuffering(id, true)}
      //onBufferEnd={() => onIsBuffering && onIsBuffering(id, false)}
      onPlay={onPlay}
      onPause={onPause}
      controls={showControls}
      progressInterval={intervalMs}
      onProgress={onProgress}
      onDuration={onDuration}
      onSeek={seekReady}
    />
  )
}

export default VideoPlayer